/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table as UITable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TableLoad } from "@/components/ui/table-load";
import { EmptyTable } from "@/components/Datatable/components/EmptyTable";
import { ExtendedColumnDef } from "components/Datatable";
import { useState, useEffect, useMemo } from "react";
import { Meta } from "src/@types/paginated";
import { PaginationButtons } from "components/PaginatedDatatable/components/PaginationButtons";
import styled from "styled-components";

interface ITableProps<T> {
  data: any[];
  columns: ExtendedColumnDef<T>[];
  isFetching?: boolean;
}

export default function Table({ data, columns, isFetching }: ITableProps<any>) {
  const [pagination, setPagination] = useState<Meta>({
    currentPage: 1,
    total: Math.ceil(data.length / 20),
    next: 2,
    prev: null,
    lastPage: Math.ceil(data.length / 20),
    perPage: 20,
  });

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      total: Math.ceil(data.length / 20),
      lastPage: Math.ceil(data.length / 20),
      perPage: 20,
      currentPage: Math.min(prev.currentPage, Math.ceil(data.length / 20) || 1),
      next: Math.min(prev.currentPage + 1, Math.ceil(data.length / 20)),
      prev: prev.currentPage > 1 ? prev.currentPage - 1 : null,
    }));
  }, [data]);

  const paginatedData = useMemo(
    () => data.slice(
      (pagination.currentPage - 1) * 20,
      pagination.currentPage * 20
    ),
    [data, pagination.currentPage]
  );

  const table = useReactTable({
    data: paginatedData as any,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <PaginatedDataTableContainer>
      <div className="rounded-t-md border max-h-[500px] overflow-auto custom-scrollbar relative">
        <UITable className="relative">
          <TableHeader className="sticky top-0 bg-white z-10">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const columnDef = header.column.columnDef as any;
                  return (
                    <TableHead
                      key={header.id}
                      colSpan={header.colSpan}
                      className={columnDef.className}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isFetching ? (
              <TableLoad colSpan={columns.length} />
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className="hover:bg-gray-100"
                >
                  {row.getVisibleCells().map((cell) => {
                    const columnDef = cell.column.columnDef as any;
                    return (
                      <TableCell key={cell.id} className={columnDef.className}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <EmptyTable text="Selecione uma data de conciliação para ver os dados." />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </UITable>
      </div>
      {pagination.total > 1 && (
        <PaginationButtons
          pagination={pagination}
          setPagination={setPagination}
        />
      )}
    </PaginatedDataTableContainer>
  );
}

const PaginatedDataTableContainer = styled.div`
  display: flex;
  flex-direction: column;


  .table-form-scope {
    width: 100%;

    * {
      border-radius: 0;
    }

    button {
      height: 30px !important;
      padding-top: 0 !important;
      padding-bottom: 0 !important;
      border: none !important;
    }
  }

  .select-table {
    button {
      &.selected {
        color: #444444;
      }
      &.not-selected {
        color: #b3b3b3;
      }
    }

    &:hover {
      background-color: #dcdce3;
    }
  }

  .reponsible-column,
  .region-column {
    padding: 0;
    width: 30%;
  }

  .store-column {
    width: 30%;
  }
`;
